import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useAppSelector } from '../../../contexts/hooks';
import TreeView from 'devextreme-react/tree-view';

import { Footer } from '../..';
import { navigation } from '../../../app-navigation';
import { useNavigation } from '../../../contexts/navigation';
import { useScreenSize } from '../../../utils/media-query';
import { formatMessage } from 'devextreme/localization';
import { isMenuItemNew } from '../../../utils/menu-utils';

import type { SideNavigationMenuProps } from '../../../types';

import './SideNavigationMenu.scss';

import * as events from 'devextreme/events';
import { MenuPpal } from '../../../types/types.system';

export const SideNavigationMenu = (props: React.PropsWithChildren<SideNavigationMenuProps>) => {
  const permissions = useAppSelector((x) => x.userInfo.Permissions);
  const initialConfig = useAppSelector((x) => x.configuration.Data?.Config_General);
  const { children, selectedItemChanged, openMenu, onMenuReady } = props;

  const { isLarge } = useScreenSize();
  function normalizePath() {
    const filteredNavigation = navigation.filter((item: MenuPpal) => {
      const permission = item.permission;
      if (item.module && initialConfig?.find((x) => x.Cgn_Nombre === item.module)?.Cgn_Resultado === 'false') return false;
      return !permission || permissions?.[permission];
    });
    return filteredNavigation
      .map((item: any) => ({
        ...item,
        text: formatMessage(item.text),
        items: normalizeSubPath(item.items),
        expanded: isLarge,
        path: item.path && !/^\//.test(item.path) ? `/${item.path}` : item.path,
        fechaLanzamiento: item.fechaLanzamiento,
        fechaFin: item.fechaFin,
        isNew: isMenuItemNew(item.fechaLanzamiento, item.fechaFin),
      }))
      // Un grupo que se quedo sin hijos visibles y que no tiene pantalla propia no lleva a
      // ningun lado: se pintaria como un acordeon que se abre y esta vacio. Se descarta.
      //
      // Hace falta desde que el grupo de Control de calidad dejo de exigir un permiso propio:
      // ahora lo gobierna el modulo, asi que sin esto lo veria tambien quien no tiene ningun
      // permiso de calidad. Los grupos con pantalla propia (item.path) no se tocan.
      .filter((item: any) => Boolean(item.path) || !Array.isArray(item.items) || item.items.length > 0);
  }
  function normalizeSubPath(subItems) {
    if (!Array.isArray(subItems)) {
      return subItems;
    }

    const filteredNavigation = subItems.filter((item: any) => {
      const permission = item.permission;
      if (item.module && initialConfig?.find((x) => x.Cgn_Nombre === item.module)?.Cgn_Resultado === 'false') return false;
      return !permission || permissions?.[permission];
    });
    return filteredNavigation.map((item: any) => ({
      ...item,
      text: formatMessage(item.text),
      expanded: false,
      path: item.path && !/^\//.test(item.path) ? `/${item.path}` : item.path,
      fechaLanzamiento: item.fechaLanzamiento,
      fechaFin: item.fechaFin,
      isNew: isMenuItemNew(item.fechaLanzamiento, item.fechaFin),
    }));
  }
  const items = useMemo(normalizePath, []);

  const {
    navigationData: { currentPath },
  } = useNavigation();

  const treeViewRef = useRef<TreeView>(null);
  const wrapperRef = useRef();
  const getWrapperRef = useCallback(
    (element: any) => {
      const prevElement = wrapperRef.current;
      if (prevElement) {
        events.off(prevElement, 'dxclick');
      }

      wrapperRef.current = element;
      events.on(element, 'dxclick', (e: React.PointerEvent) => {
        openMenu(e);
      });
    },
    [openMenu]
  );

  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance;
    if (!treeView) {
      return;
    }

    if (currentPath !== undefined) {
      treeView.selectItem(currentPath);
      treeView.expandItem(currentPath);
    }
  }, [currentPath]);
  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance;
    if (!treeView) {
      return;
    }

    treeView.collapseItem('/configuration');    // Agregar chips "Nuevo" después de que el menú esté renderizado
    setTimeout(() => {
      addNewChipsToMenu();
    }, 300);
  }, []); // Función específica para debuggear submenús
  // Función para agregar chips "Nuevo" a los elementos del menú
  const addNewChipsToMenu = () => {
    // NIVEL 1: Menú principal
    const treeViewContainer = document.querySelector('.side-navigation-menu .dx-treeview');
    if (!treeViewContainer) {
      console.error('⚠️ No se encontró el contenedor del TreeView');
      return;
    }

    // Buscar todos los nodos de nivel 1
    const level1Nodes = treeViewContainer.querySelectorAll('.dx-treeview-node[aria-level="1"]');
    level1Nodes.forEach((node, index) => {
      const itemElement = node.querySelector('.dx-treeview-item');
      if (!itemElement) return;

      const htmlElement = itemElement as HTMLElement;
      
      // Buscar el item correspondiente por índice
      const itemData = items[index];
      if (!itemData) return;

      if (isMenuItemNew(itemData.fechaLanzamiento, itemData.fechaFin)) {
        const daysSinceLaunch = itemData.fechaLanzamiento ? (new Date().getTime() - new Date(itemData.fechaLanzamiento).getTime()) / (1000 * 60 * 60 * 24) : 999;
        const isVeryNew = daysSinceLaunch <= 7;        const chipElement = document.createElement('span');
        chipElement.className = `new-menu-chip ${isVeryNew ? 'very-new-item' : 'new-item'}`;
        chipElement.style.cssText = `
          background: linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%);
          color: #ffffff;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 0.45rem;
          font-weight: 500;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          white-space: nowrap;
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          pointer-events: none;
          opacity: 0.9;
        `;// Eliminar la animación CSS previa si existe
        const existingStyle = document.getElementById('chip-animation-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
        chipElement.textContent = 'NUEVO';
        chipElement.title = `Lanzado el ${new Date(itemData.fechaLanzamiento || '').toLocaleDateString('es-ES')}`;        // Asegurar posicionamiento relativo
        htmlElement.style.position = 'relative';
        htmlElement.appendChild(chipElement);
      }
    });
    // Esperar a que los submenús estén disponibles
    setTimeout(() => {
      const level2Items = document.querySelectorAll('.side-navigation-menu .dx-treeview-node[aria-level="2"]');

      if (level2Items.length === 0) return;

      level2Items.forEach((node, elementIndex) => {
        const menuElement = node.querySelector('.dx-treeview-item');
        if (!menuElement) return;

        const htmlElement = menuElement as HTMLElement;
        const itemElement = node as HTMLElement;
        const dataItemId = itemElement.getAttribute('data-item-id');

        // Buscar en todos los subitems por PATH
        let itemFound = false;
        items.forEach((parentItem) => {
          if (parentItem.items && !itemFound) {
            parentItem.items.forEach((subItem) => {
              const normalizedPath = subItem.path && !/^\//.test(subItem.path) ? `/${subItem.path}` : subItem.path;
              const pathMatches = dataItemId === normalizedPath || dataItemId === subItem.path || (dataItemId && normalizedPath && dataItemId.endsWith(normalizedPath)) || (dataItemId && subItem.path && dataItemId.endsWith(subItem.path));

              if (pathMatches) {
                if (isMenuItemNew(subItem.fechaLanzamiento, subItem.fechaFin)) {
                  if (!htmlElement.querySelector('.new-menu-chip')) {
                    const daysSinceLaunch = subItem.fechaLanzamiento ? (new Date().getTime() - new Date(subItem.fechaLanzamiento).getTime()) / (1000 * 60 * 60 * 24) : 999;
                    const isVeryNew = daysSinceLaunch <= 7;                    const chipElement = document.createElement('span');
                    chipElement.className = `new-menu-chip ${isVeryNew ? 'very-new-item' : 'new-item'}`;
                    chipElement.style.cssText = `
                      background: linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%);
                      color: #ffffff;
                      border-radius: 10px;
                      padding: 1px 6px;
                      font-size: 0.42rem;
                      font-weight: 500;
                      letter-spacing: 0.5px;
                      text-transform: uppercase;
                      white-space: nowrap;
                      position: absolute;
                      right: 8px;
                      top: 50%;
                      transform: translateY(-50%);
                      z-index: 20;
                      pointer-events: none;
                      opacity: 0.85;
                    `;
                    chipElement.textContent = 'NUEVO';
                    chipElement.title = `Lanzado el ${new Date(subItem.fechaLanzamiento || '').toLocaleDateString('es-ES')}`;

                    htmlElement.style.position = 'relative';
                    htmlElement.appendChild(chipElement);
                    itemFound = true;
                  }
                }
                itemFound = true;
              }
            });
          }
        });
      });
    }, 100);
  };

  return (
    <div className='dx-swatch-additional side-navigation-menu' ref={getWrapperRef}>
      {children}{' '}
      <div className='menu-container'>
        <TreeView
          ref={treeViewRef}
          items={items}
          keyExpr='path'
          selectionMode='single'
          focusStateEnabled={false}
          expandEvent='click'
          onItemClick={selectedItemChanged}          onContentReady={(e) => {
            onMenuReady && onMenuReady(e);
            // Agregar chips después de que el contenido esté listo
            setTimeout(() => {
              addNewChipsToMenu();
            }, 150);
          }}
          onItemExpanded={(e) => {
            // Ejecutar varias veces con diferentes delays para asegurar que los submenús estén renderizados
            setTimeout(() => {
              addNewChipsToMenu();
            }, 50);

            setTimeout(() => {
              addNewChipsToMenu();
            }, 200);

            setTimeout(() => {
              addNewChipsToMenu();
            }, 500);
          }}
          width='100%'
        />
      </div>
      <Footer>
        Copyright © {new Date().getFullYear()} <br /> NEURASOFT S.A.S
      </Footer>
    </div>
  );
};
